import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  Compass,
  GraduationCap,
  Globe,
  Handshake,
  Menu,
  Newspaper,
  ShieldCheck,
  X,
} from 'lucide-react';

import { Button, Logo, cn } from '@ibas/ui';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { DURATION, EASE } from '@/lib/motion-tokens';

interface NavLinkItem {
  label: string;
  href: string;
  /** Short one-line context shown under the label in the mega panel. */
  description?: string;
}

interface NavColumn {
  heading: string;
  icon?: typeof Globe;
  items: NavLinkItem[];
}

interface NavTab {
  key: string;
  label: string;
  /** Where the tab label itself links to (its landing page). */
  href: string;
  columns: NavColumn[];
}

const TABS: NavTab[] = [
  {
    key: 'programmes',
    label: 'Programmes',
    href: '/programmes',
    columns: [
      {
        heading: 'Online',
        icon: Globe,
        items: [
          {
            label: 'Introduction to online learning',
            href: '/programmes/online-learning',
            description: 'How the flexible, fully online Flexi-Learn model works.',
          },
          {
            label: 'Flexi-MBA',
            href: '/programmes/flexi-mba',
            description: 'Rolling monthly modules, min. 8 months, 60–90 ECTS.',
          },
          {
            label: 'Flexi-DBA',
            href: '/programmes/flexi-dba',
            description: 'Doctoral study paced around your career.',
          },
        ],
      },
      {
        heading: 'On-site',
        icon: GraduationCap,
        items: [
          { label: 'Diploma Programme', href: '/programmes/diploma' },
          {
            label: 'Bachelor of Business Administration',
            href: '/programmes/bba',
            description: 'BBA, General.',
          },
          { label: "Master's Programmes", href: '/programmes/masters' },
        ],
      },
    ],
  },
  {
    key: 'admissions',
    label: 'Admissions',
    href: '/admissions',
    columns: [
      {
        heading: 'Admissions',
        items: [
          { label: 'Why study with IBAS', href: '/admissions#why-ibas' },
          { label: 'Find your advisor', href: '/admissions#find-advisor' },
          { label: 'Admission process', href: '/admissions#process' },
          { label: 'Build your study track', href: '/admissions#custom-track' },
          { label: 'FAQs', href: '/admissions#faqs' },
        ],
      },
    ],
  },
  {
    key: 'experience',
    label: 'IBAS Experience',
    href: '/experience',
    columns: [
      {
        heading: 'Life at IBAS',
        icon: Compass,
        items: [
          {
            label: 'Campus Thalwil',
            href: '/experience#campus',
            description: 'The on-site campus just outside Zurich.',
          },
          {
            label: 'Andragogical principles',
            href: '/experience#andragogy',
            description: 'The adult-centred way IBAS teaches.',
          },
          {
            label: 'Gallery',
            href: '/experience#gallery',
            description: 'Moments from campus and community.',
          },
        ],
      },
      {
        heading: 'Outcomes',
        icon: Award,
        items: [
          {
            label: 'Student achievement rates',
            href: '/experience#achievement-rates',
            description: 'ACBSP retention and learning outcomes.',
          },
          {
            label: 'Internship programme',
            href: '/experience#internship',
            description: 'Remote, global and on-site pathways.',
          },
          {
            label: 'Alumni',
            href: '/experience#alumni',
            description: 'A lasting, international community.',
          },
        ],
      },
    ],
  },
  {
    key: 'about',
    label: 'About',
    href: '/about',
    columns: [
      {
        heading: 'Who we are',
        icon: Compass,
        items: [
          {
            label: 'Vision & mission',
            href: '/about#vision-mission',
            description: 'What IBAS stands for and how we deliver it.',
          },
          {
            label: 'Management & advisory board',
            href: '/about#leadership',
            description: 'The people leading IBAS.',
          },
          {
            label: 'International faculty',
            href: '/about#faculty',
            description: 'Educators drawn from across the world.',
          },
          {
            label: 'Accreditations & memberships',
            href: '/about#accreditations',
            description: 'ACBSP, QS, ISASAHE, IACBE and more.',
          },
        ],
      },
      {
        heading: 'Governance & contact',
        icon: ShieldCheck,
        items: [
          { label: 'EduQua certification', href: '/about#eduqua' },
          { label: 'Quality assurance', href: '/about#quality' },
          { label: 'Social responsibility', href: '/about#social-responsibility' },
          { label: 'Code of conduct', href: '/about#code-of-conduct' },
          { label: 'Contact us', href: '/about#contact' },
        ],
      },
    ],
  },
  {
    key: 'current-students',
    label: 'Current Students',
    href: '/current-students',
    columns: [
      {
        heading: 'Current students',
        icon: GraduationCap,
        items: [
          {
            label: 'International Students Office',
            href: '/current-students#international-office',
            description: 'Arrival, permits, insurance and on-site support.',
          },
          {
            label: 'Events & social programmes',
            href: '/current-students#events',
            description: 'Community, networking and student life.',
          },
          {
            label: 'Campus Thalwil events',
            href: '/current-students#campus-events',
            description: 'On-site moments — welcomes and graduations.',
          },
        ],
      },
    ],
  },
  {
    key: 'partners',
    label: 'Partners',
    href: '/partners',
    columns: [
      {
        heading: 'Our network',
        icon: Handshake,
        items: [
          {
            label: 'Academic Partners',
            href: '/partners#academic-partners',
            description: 'Universities and colleges collaborating with IBAS.',
          },
          {
            label: 'Learning Center Partners',
            href: '/partners#learning-center-partners',
            description: 'Regional centres delivering IBAS learning.',
          },
          {
            label: 'Agents',
            href: '/partners#agents',
            description: 'Authorised admissions representatives worldwide.',
          },
        ],
      },
    ],
  },
  {
    key: 'news',
    label: 'News & Events',
    href: '/news',
    columns: [
      {
        heading: 'Newsroom',
        icon: Newspaper,
        items: [
          {
            label: 'All news & events',
            href: '/news',
            description: 'Every current story, newest first.',
          },
          {
            label: 'Accreditations & rankings',
            href: '/news?category=Accreditation+%26+Rankings',
            description: 'QS, ISASAHE and quality milestones.',
          },
          {
            label: 'Graduations',
            href: '/news?category=Graduation',
            description: 'Ceremonies and graduate success.',
          },
          {
            label: 'Campus & community',
            href: '/news?category=Campus+%26+Community',
            description: 'Life, events and moments at IBAS.',
          },
        ],
      },
    ],
  },
];

interface TabRect {
  left: number;
  width: number;
}

/**
 * IBAS mega-nav: an animated-tab / sliding-panel / nub mega menu.
 *
 * Motion intent: a shared `layoutId` highlight glides between tab triggers on
 * hover/focus (the "nub"), and a single mega panel below cross-fades its
 * column content while sliding in from the direction the user is moving
 * through the tabs — the "shifting dropdown" mechanic. Panel presence is
 * handled by `AnimatePresence` (mount/unmount) per the house engine-selection
 * rule for React-state-driven interactions.
 *
 * Reduced motion: the panel still opens/closes and the active tab is still
 * highlighted — only the x-offset slide and the spring glide of the nub are
 * removed, so the menu is instantly functional and readable.
 */
export interface MegaNavProps {
  className?: string;
}

export function MegaNav({ className }: MegaNavProps) {
  const reduced = useReducedMotion();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rects, setRects] = useState<Record<string, TabRect>>({});
  /** True once the page has scrolled off the hero — flips the overlay bar solid. */
  const [scrolled, setScrolled] = useState(false);
  /** True while the visitor is scrolling *down* the page — auto-hides the bar. */
  const [hidden, setHidden] = useState(false);

  const navRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const activeIndex = openKey ? TABS.findIndex((t) => t.key === openKey) : -1;
  const lastIndexRef = useRef(0);
  if (activeIndex >= 0) lastIndexRef.current = activeIndex;

  const measure = () => {
    const row = rowRef.current;
    if (!row) return;
    const rowBox = row.getBoundingClientRect();
    const next: Record<string, TabRect> = {};
    for (const tab of TABS) {
      const el = tabRefs.current[tab.key];
      if (!el) continue;
      const box = el.getBoundingClientRect();
      next[tab.key] = { left: box.left - rowBox.left, width: box.width };
    }
    setRects(next);
  };

  useLayoutEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Two scroll-driven behaviours, both class-toggles (safe under reduced motion,
    // where the translate is additionally gated out in render):
    //   • `scrolled`: transparent scrim over the dark hero at the top → solid
    //     readable bar once scrolled onto light content.
    //   • `hidden`: auto-hide on scroll-down, reveal on scroll-up. Always shown
    //     near the top so the bar can never be stranded off-screen at page start.
    // Reads are rAF-throttled and a small delta threshold ignores scroll jitter.
    const REVEAL_ZONE = 96; // px from top where the bar is always visible
    const DELTA = 6; // ignore sub-threshold wobble
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = Math.max(window.scrollY, 0);
      setScrolled(y > 64);
      if (y <= REVEAL_ZONE) {
        setHidden(false);
      } else if (y > lastY + DELTA) {
        setHidden(true); // scrolling down
      } else if (y < lastY - DELTA) {
        setHidden(false); // scrolling up
      }
      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenKey(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(event.target as Node)) {
        setOpenKey(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenKey(null), 140);
  };

  const openTab = (key: string) => {
    cancelClose();
    setOpenKey(key);
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      const next = TABS[(index + 1) % TABS.length];
      tabRefs.current[next.key]?.focus();
      openTab(next.key);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const prev = TABS[(index - 1 + TABS.length) % TABS.length];
      tabRefs.current[prev.key]?.focus();
      openTab(prev.key);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      openTab(TABS[index].key);
    }
  };

  const activeTab = TABS.find((t) => t.key === openKey) ?? null;
  const activeRect = openKey ? rects[openKey] : undefined;
  const direction = activeIndex >= lastIndexRef.current ? 1 : -1;

  const nubX = (() => {
    if (!activeRect || !rowRef.current) return 0;
    const rowWidth = rowRef.current.getBoundingClientRect().width;
    const center = activeRect.left + activeRect.width / 2;
    const half = 7;
    return Math.min(Math.max(center, half), Math.max(rowWidth - half, half));
  })();

  // Auto-hide the bar only while scrolling down on real content — never while a
  // mega/mobile menu is open, and never under reduced motion (it then stays put).
  const barHidden = hidden && !openKey && !mobileOpen && !reduced;

  return (
    <nav
      ref={navRef}
      onMouseLeave={scheduleClose}
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[transform,background-color,border-color,box-shadow] duration-300 will-change-transform',
        barHidden ? '-translate-y-full' : 'translate-y-0',
        scrolled
          ? 'border-b border-border/60 bg-background/85 shadow-sm backdrop-blur-md'
          : 'border-b border-transparent bg-gradient-to-b from-black/25 to-transparent',
        className,
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4 lg:px-8">
        {/* Logo sits directly on the page (no chip). */}
        <Link to="/" className="shrink-0" aria-label="IBAS home">
          {/* Wordmark glows subtly while overlaying the dark hero, plain once scrolled. */}
          <Logo
            className={cn(
              'w-[130px] transition-[filter] duration-300 lg:w-[150px]',
              !scrolled && 'drop-shadow-[0_1px_10px_rgba(255,255,255,0.35)]',
            )}
          />
        </Link>

        {/* Small rounded pill behind the links only — the nav bar itself is transparent. */}
        <div
          ref={rowRef}
          className="relative hidden items-center gap-1 rounded-full border border-border/60 bg-background/80 px-2 py-1.5 shadow-sm backdrop-blur-md lg:flex"
        >
          {TABS.map((tab, index) => {
            const isOpen = openKey === tab.key;
            return (
              <button
                key={tab.key}
                ref={(el) => {
                  tabRefs.current[tab.key] = el;
                }}
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onMouseEnter={() => openTab(tab.key)}
                onFocus={() => openTab(tab.key)}
                onKeyDown={(e) => onTabKeyDown(e, index)}
                onClick={() => setOpenKey((k) => (k === tab.key ? null : tab.key))}
                className={cn(
                  'relative rounded-full px-4 py-2 font-sans text-sm font-medium text-foreground/80 transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none',
                  isOpen && 'text-foreground',
                )}
              >
                {isOpen && (
                  <motion.span
                    layoutId="mega-nav-highlight"
                    className="absolute inset-0 -z-10 rounded-full bg-muted"
                    transition={
                      reduced
                        ? { duration: 0 }
                        : { type: 'tween', duration: DURATION.fast, ease: EASE.out }
                    }
                  />
                )}
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <Button asChild variant="primary" size="md" className="hidden lg:inline-flex">
            <Link to="/apply">
              Apply now
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
          <button
            type="button"
            className="inline-flex size-10 items-center justify-center rounded-md text-foreground lg:hidden"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Desktop mega panel */}
      <div className="relative mx-auto hidden max-w-7xl px-6 lg:block lg:px-8">
        <AnimatePresence>
          {activeTab && (
            <motion.div
              key="mega-panel"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
              transition={{ duration: reduced ? DURATION.micro : DURATION.fast, ease: EASE.out }}
              className="absolute inset-x-0 top-2 z-40 overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-xl"
            >
              {!reduced && (
                <motion.span
                  aria-hidden
                  className="absolute -top-1.5 size-3 rotate-45 rounded-[2px] border-t border-l border-border bg-popover"
                  animate={{ left: nubX }}
                  initial={false}
                  transition={{ type: 'tween', duration: DURATION.fast, ease: EASE.out }}
                  style={{ marginLeft: -6 }}
                />
              )}

              <AnimatePresence mode="wait" initial={false} custom={direction}>
                <motion.div
                  key={activeTab.key}
                  custom={direction}
                  initial={reduced ? { opacity: 0 } : { opacity: 0, x: 24 * direction }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={reduced ? { opacity: 0 } : { opacity: 0, x: -24 * direction }}
                  transition={{
                    duration: reduced ? DURATION.micro : DURATION.fast,
                    ease: EASE.out,
                  }}
                  className={cn(
                    'grid gap-8 p-8',
                    activeTab.columns.length > 1 ? 'grid-cols-2' : 'max-w-md grid-cols-1',
                  )}
                >
                  {activeTab.columns.map((column) => (
                    <div key={column.heading} className="space-y-3">
                      <p className="flex items-center gap-2 font-label text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                        {column.icon && <column.icon className="size-3.5" aria-hidden />}
                        {column.heading}
                      </p>
                      <ul className="space-y-1">
                        {column.items.map((item) => (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              onClick={() => setOpenKey(null)}
                              className="group flex items-start justify-between gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-muted"
                            >
                              <span>
                                <span className="block font-sans text-sm font-medium text-foreground transition-colors group-hover:text-brand-gold">
                                  {item.label}
                                </span>
                                {item.description && (
                                  <span className="mt-0.5 block text-sm text-muted-foreground">
                                    {item.description}
                                  </span>
                                )}
                              </span>
                              <ArrowRight
                                className="mt-0.5 size-4 shrink-0 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:text-brand-gold group-hover:opacity-100"
                                aria-hidden
                              />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  <div
                    className={cn(
                      'flex items-end',
                      activeTab.columns.length > 1 && 'col-span-2 border-t border-border pt-4',
                    )}
                  >
                    <Link
                      to={activeTab.href}
                      onClick={() => setOpenKey(null)}
                      className="inline-flex items-center gap-1.5 font-label text-xs font-semibold tracking-wider text-foreground uppercase transition-colors hover:text-brand-gold"
                    >
                      View all {activeTab.label.toLowerCase()}
                      <ArrowRight className="size-3.5" aria-hidden />
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          'grid lg:hidden',
          'transition-[grid-template-rows] duration-300 ease-out',
          mobileOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden border-t border-border bg-popover text-popover-foreground">
          <div className="space-y-6 px-6 py-6">
            {TABS.map((tab) => (
              <div key={tab.key} className="space-y-2">
                <Link
                  to={tab.href}
                  onClick={() => setMobileOpen(false)}
                  className="block font-display text-lg text-foreground"
                >
                  {tab.label}
                </Link>
                <ul className="space-y-1 pl-3">
                  {tab.columns
                    .flatMap((c) => c.items)
                    .map((item) => (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="block py-1.5 text-sm text-muted-foreground transition-colors hover:text-brand-gold"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
            <Button asChild variant="primary" size="md" className="w-full">
              <Link to="/apply" onClick={() => setMobileOpen(false)}>
                Apply now
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}
